// Turns for the secretary's raw-mic tap. A worklet frame message received before release proves that the audio
// thread had already processed through that frame. Never infer this boundary from a rounded page-facing clock.
const JOIN_MS = 200, FLUSH_TIMEOUT_MS = 1000, MAX_PENDING_SAMPLES = 16000, MAX_WINDOWS = 8;

export function createSecretaryCapture({ ctx, port, send, valid, failed }) {
  let disposed = false, active = null, currentTurn = 0, nextTurn = 0, nextId = 0, pending = 0;
  let windows = [], joinTimer = null, joinUntil = 0, lastReleased = null, graphGeneration = 0;
  const graphChanged = () => { graphGeneration++; };
  ctx.addEventListener?.('statechange', graphChanged);
  const write = (value) => { try { return valid() && send(value) !== false; } catch { return false; } };
  function dispose() {
    if (disposed) return;
    disposed = true; clearTimeout(joinTimer);
    for (const w of windows) clearTimeout(w.timer);
    windows = []; active = null; lastReleased = null; pending = 0; currentTurn = 0;
    ctx.removeEventListener?.('statechange', graphChanged);
    port.onmessage = null;
    try { port.postMessage({ t: 'cancel' }); } catch {}
  }
  function fail() { if (!disposed) { dispose(); failed(); } }
  function enterTurn(turn) {
    if (currentTurn === turn) return true;
    if (currentTurn && !write(JSON.stringify({ t: 'ptt', on: false }))) return false;
    currentTurn = 0;
    if (!write(JSON.stringify({ t: 'ptt', on: true }))) return false;
    currentTurn = turn; return true;
  }
  function finishIfReady() {
    if (disposed || active || windows.length || !currentTurn) return;
    clearTimeout(joinTimer);
    const wait = joinUntil - performance.now();
    if (wait > 0) { joinTimer = setTimeout(() => { joinTimer = null; finishIfReady(); }, wait); return; }
    if (!write(JSON.stringify({ t: 'ptt', on: false }))) return fail();
    currentTurn = 0;
  }
  function resolveTurn(w) {
    if (w.turn) return true;
    const previous = w.previous;
    if (!previous.turn || (w.firstFrame === null && !w.done)) return false;
    // The previous frame was acknowledged before release; this one was processed after the new start.
    // Their difference is an upper bound on the gap in the running audio graph, independent of clock rounding.
    const gap = w.firstFrame === null ? Infinity : w.firstFrame - previous.end;
    const same = gap >= 0 && gap <= ctx.sampleRate * JOIN_MS / 1000 && ctx.state === 'running' &&
      previous.graphGeneration === w.graphGeneration && graphGeneration === w.graphGeneration;
    w.turn = same ? previous.turn : ++nextTurn;
    w.previous = null; return true;
  }
  function pump() {
    if (disposed) return;
    if (!valid()) return fail();
    while (windows.length) {
      const w = windows[0];
      if (!resolveTurn(w)) break;
      if (!enterTurn(w.turn)) return fail();
      const limit = w.end === null ? w.observed : w.end;
      while (w.parts.length) {
        const p = w.parts[0]; let n = p.pos;
        while (n < p.pcm.length && p.ends[n] <= limit) n++;
        if (n > p.pos) {
          const pcm = p.pcm.slice(p.pos, n).buffer;
          if (!write(pcm)) return fail();
          pending -= n - p.pos; p.pos = n;
        }
        if (p.pos === p.pcm.length) w.parts.shift();
        else if (w.end !== null) { pending -= p.pcm.length - p.pos; w.parts.shift(); } // after release: drop it
        else break; // wait for a worklet frame acknowledgement
      }
      if (w.parts.length || !w.done || w.end === null) break;
      clearTimeout(w.timer); windows.shift();
    }
    finishIfReady();
  }
  port.onmessage = ({ data }) => {
    if (disposed || !data || (data.t !== 'pcm' && data.t !== 'frame' && data.t !== 'done')) return;
    const w = windows.find((x) => x.id === data.id);
    if (!w || w.done) return; // a late packet from a cancelled or already finished window
    if (data.t === 'done') { w.done = true; pump(); return; }
    if (data.t === 'frame') {
      if (!Number.isSafeInteger(data.end) || data.end < 0) return fail();
      if (w.firstFrame === null) w.firstFrame = data.end;
      if (w.end === null) w.observed = Math.max(w.observed, data.end);
      pump(); return;
    }
    if (!(data.pcm instanceof ArrayBuffer) || !(data.ends instanceof ArrayBuffer)) return fail();
    let pcm, ends;
    try { pcm = new Int16Array(data.pcm); ends = new Float64Array(data.ends); } catch { return fail(); }
    if (!pcm.length || pcm.length > 1600 || ends.length !== pcm.length) return fail();
    for (let i = 0; i < ends.length; i++) if (!Number.isSafeInteger(ends[i]) || ends[i] < 0 || (i && ends[i] < ends[i - 1])) return fail();
    if (w.firstFrame === null) w.firstFrame = ends[ends.length - 1];
    // This message was produced by the audio thread before the page received it. Only use it as an observation
    // while the window is still held; a delayed post-release message may contain newer audio.
    if (w.end === null) w.observed = Math.max(w.observed, ends[ends.length - 1]);
    pending += pcm.length;
    if (pending > MAX_PENDING_SAMPLES) return fail();
    w.parts.push({ pcm, ends, pos: 0 });
    pump();
  };
  return {
    start() {
      if (disposed || active || !valid() || windows.length >= MAX_WINDOWS) { if (!disposed) fail(); return false; }
      clearTimeout(joinTimer); joinTimer = null;
      // Joining also needs proof from audio-thread frames; a rounded page clock alone is not enough.
      const previous = currentTurn && lastReleased?.end && ctx.state === 'running' && performance.now() <= joinUntil ? lastReleased : null;
      const w = { id: ++nextId, turn: previous ? 0 : ++nextTurn, previous, firstFrame: null, graphGeneration,
        observed: 0, end: null, done: false, parts: [], timer: null };
      windows.push(w); active = w; pump();
      if (disposed) return false;
      try { port.postMessage({ t: 'start', id: w.id }); } catch { fail(); return false; }
      return true;
    },
    release() {
      if (disposed || !active) return;
      const w = active; active = null; w.end = w.observed; lastReleased = w; joinUntil = performance.now() + JOIN_MS;
      w.timer = setTimeout(() => { if (windows.includes(w)) fail(); }, FLUSH_TIMEOUT_MS);
      try { port.postMessage({ t: 'stop', id: w.id }); } catch { return fail(); }
      pump();
    },
    running: () => !!currentTurn || !!active || !!windows.length,
    dispose,
  };
}
